import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import StatsCard from '../Common/StatsCard';
import Modal from '../Common/Modal';

const STATUS_META = {
  open:      { label: 'Open',      color: 'bg-yellow-100 text-yellow-800' },
  follow_up: { label: 'Follow Up', color: 'bg-blue-100 text-blue-800' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-800' },
  closed:    { label: 'Closed',    color: 'bg-gray-100 text-gray-700' },
};

const INQUIRY_TYPES = {
  admission: { label: 'Admission', icon: 'fa-user-graduate', color: 'bg-blue-100 text-blue-800' },
  general:   { label: 'General',   icon: 'fa-info-circle',    color: 'bg-gray-100 text-gray-700' },
  fee:       { label: 'Fee',       icon: 'fa-credit-card',    color: 'bg-green-100 text-green-800' },
  transfer:  { label: 'Transfer',  icon: 'fa-exchange-alt',   color: 'bg-purple-100 text-purple-800' },
  other:     { label: 'Other',     icon: 'fa-ellipsis-h',     color: 'bg-yellow-100 text-yellow-800' },
};

const INQUIRY_TYPE_OPTIONS = [
  { value: 'admission', label: 'Admission' },
  { value: 'general',   label: 'General' },
  { value: 'fee',       label: 'Fee' },
  { value: 'transfer',  label: 'Transfer' },
  { value: 'other',     label: 'Other' },
];

const EMPTY_FORM = {
  visitor_name: '', phone: '', email: '',
  inquiry_type: 'admission', child_name: '',
  child_dob: '', class_interested: '',
  notes: '', status: 'open', follow_up_date: '',
};

export default function ReceptionistDashboard() {
  const { user } = useAuth();

  // ── Announcements state ──
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  // ── Reception data state ──
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [receptionLoading, setReceptionLoading] = useState(true);
  const [receptionError, setReceptionError] = useState('');

  // ── Quick inline search ──
  const [quickSearch, setQuickSearch] = useState('');
  const [showQuickResults, setShowQuickResults] = useState(false);

  // ── Quick Inquiry modal ──
  const [showQuickInquiry, setShowQuickInquiry] = useState(false);
  const [quickForm, setQuickForm] = useState(EMPTY_FORM);
  const [savingQuick, setSavingQuick] = useState(false);

  // ═══════════════════════════════════════
  // Load data
  // ═══════════════════════════════════════
  useEffect(() => {
    api
      .get('/announcements/')
      .then(r => setAnnouncements(r.data || []))
      .catch(() => setAnnouncements([]))
      .finally(() => setAnnouncementsLoading(false));

    loadReception();
  }, []);

  const loadReception = async () => {
    setReceptionLoading(true);
    setReceptionError('');
    try {
      const [c, i] = await Promise.all([
        api.get('/reception/contacts'),
        api.get('/reception/inquiries'),
      ]);
      setContacts(c.data || []);
      setInquiries(i.data || []);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load reception data';
      setReceptionError(msg);
      console.error('[reception] error:', err.response || err);
    } finally {
      setReceptionLoading(false);
    }
  };

  // ═══════════════════════════════════════
  // Derived data
  // ═══════════════════════════════════════
  const recentAnnouncements = announcements.slice(0, 4);
  const unreadAnnouncements = announcements.filter(a => !a.is_read).length;

  const todayStr = new Date().toISOString().split('T')[0];

  const inquiryStats = useMemo(() => {
    const total = inquiries.length;
    const open = inquiries.filter(i => i.status === 'open').length;
    const followUp = inquiries.filter(i => i.status === 'follow_up').length;
    const converted = inquiries.filter(i => i.status === 'converted').length;
    const todayCount = inquiries.filter(
      i => i.created_at && i.created_at.startsWith(todayStr)
    ).length;
    return { total, open, followUp, converted, todayCount };
  }, [inquiries, todayStr]);

  const contactsStats = useMemo(() => {
    const students = contacts.filter(c => c.type === 'student').length;
    const parents = contacts.filter(c => c.type === 'parent').length;
    return { students, parents, total: contacts.length };
  }, [contacts]);

  const upcomingFollowUps = useMemo(() => {
    return inquiries
      .filter(i =>
        i.follow_up_date &&
        i.follow_up_date >= todayStr &&
        i.status !== 'closed' &&
        i.status !== 'converted'
      )
      .sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date))
      .slice(0, 4);
  }, [inquiries, todayStr]);

  const recentInquiries = useMemo(() => {
    return [...inquiries]
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
      .slice(0, 5);
  }, [inquiries]);

  // ═══════════════════════════════════════
  // Quick inline search over contacts
  // ═══════════════════════════════════════
  const quickResults = useMemo(() => {
    const q = quickSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return contacts
      .filter(c => {
        const blob = [
          c.name, c.phone, c.email, c.cnic,
          c.registration_number, c.class_name,
          c.guardian_name, c.guardian_phone,
          ...(c.parent_names || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      })
      .slice(0, 8);
  }, [contacts, quickSearch]);

  useEffect(() => {
    setShowQuickResults(quickSearch.trim().length >= 2);
  }, [quickSearch]);

  // ═══════════════════════════════════════
  // Quick Inquiry handlers
  // ═══════════════════════════════════════
  const openQuickInquiry = () => {
    setQuickForm(EMPTY_FORM);
    setShowQuickInquiry(true);
  };

  const saveQuickInquiry = async () => {
    if (!quickForm.visitor_name.trim()) {
      alert('Visitor name is required');
      return;
    }
    setSavingQuick(true);
    try {
      await api.post('/reception/inquiries', quickForm);
      setShowQuickInquiry(false);
      setQuickForm(EMPTY_FORM);
      await loadReception();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingQuick(false);
    }
  };

  // ═══════════════════════════════════════
  // Render
  // ═══════════════════════════════════════
  return (
    <div>
      {/* ═══════════════════════════════════════
          HERO HEADER
      ═══════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-lg shadow mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-headset text-2xl"></i>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Reception Desk</h1>
            <p className="text-teal-100 text-sm mt-1">
              Welcome back, {user?.first_name || 'Receptionist'} — here's what's happening today.
            </p>
          </div>
          <button
            onClick={loadReception}
            disabled={receptionLoading}
            className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            <i className={`fas ${receptionLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          ERROR BANNER
      ═══════════════════════════════════════ */}
      {receptionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm flex items-center gap-2">
          <i className="fas fa-exclamation-triangle"></i>
          <span><strong>Reception data error:</strong> {receptionError}</span>
        </div>
      )}

      {/* ═══════════════════════════════════════
          TOP STATS ROW
      ═══════════════════════════════════════ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatsCard
          label="Students"
          value={contactsStats.students}
          icon="fa-user-graduate"
        />
        <StatsCard
          label="Parents"
          value={contactsStats.parents}
          icon="fa-user-friends"
        />
        <StatsCard
          label="Open Inquiries"
          value={inquiryStats.open}
          icon="fa-clipboard-list"
        />
        <StatsCard
          label="Follow Ups"
          value={inquiryStats.followUp}
          icon="fa-clock"
        />
        <StatsCard
          label="Converted"
          value={inquiryStats.converted}
          icon="fa-check-circle"
        />
        <StatsCard
          label="Unread Notices"
          value={unreadAnnouncements}
          icon="fa-envelope"
        />
      </div>

      {/* ═══════════════════════════════════════
          QUICK ACTIONS — links to separate pages
      ═══════════════════════════════════════ */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">
          <i className="fas fa-bolt text-yellow-500 mr-2"></i>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Contacts Directory → its own page */}
          <Link
            to="/dashboard/contacts"
            className="bg-teal-500 text-white p-5 rounded-lg text-center hover:bg-teal-600 transition shadow-sm"
          >
            <i className="fas fa-address-book text-3xl mb-2"></i>
            <p className="font-semibold">Contacts Directory</p>
            <p className="text-xs text-teal-100 mt-1">
              Look up any student or parent
            </p>
          </Link>

          {/* Admission & Inquiries → its own page */}
          <Link
            to="/dashboard/inquiries"
            className="bg-blue-600 text-white p-5 rounded-lg text-center hover:bg-blue-700 transition shadow-sm"
          >
            <i className="fas fa-clipboard-list text-3xl mb-2"></i>
            <p className="font-semibold">Admission & Inquiries</p>
            <p className="text-xs text-blue-100 mt-1">
              Log walk-ins & track follow-ups
            </p>
          </Link>

          {/* Announcements */}
          <Link
            to="/dashboard/notifications"
            className="bg-purple-500 text-white p-5 rounded-lg text-center hover:bg-purple-600 transition shadow-sm"
          >
            <i className="fas fa-bell text-3xl mb-2"></i>
            <p className="font-semibold">Announcements</p>
            <p className="text-xs text-purple-100 mt-1">
              {unreadAnnouncements} unread notice{unreadAnnouncements !== 1 ? 's' : ''}
            </p>
          </Link>
        </div>

        {/* Secondary button: quick inquiry modal (convenience) */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={openQuickInquiry}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <i className="fas fa-bolt mr-1"></i>
            Quick-log a walk-in inquiry (no page change)
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          QUICK CONTACT LOOKUP (inline)
      ═══════════════════════════════════════ */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-2">
          <i className="fas fa-search text-teal-500 mr-2"></i>
          Quick Contact Lookup
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Type a name, phone number, CNIC, or registration number to instantly find a student or parent.
        </p>

        <div className="relative">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            placeholder="e.g. Ahmed Khan, 0300-1234567, 42101-..., REG-2025-..."
            value={quickSearch}
            onChange={e => setQuickSearch(e.target.value)}
            onFocus={() => setShowQuickResults(quickSearch.trim().length >= 2)}
          />
          {quickSearch && (
            <button
              onClick={() => setQuickSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {showQuickResults && (
          <div className="mt-3 border rounded-lg overflow-hidden bg-white">
            {quickResults.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">
                No contacts found matching "{quickSearch}".
              </p>
            ) : (
              <div className="divide-y">
                {quickResults.map(c => (
                  <div
                    key={`${c.type}-${c.id}`}
                    className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        c.type === 'student'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        <i className={`fas ${c.type === 'student' ? 'fa-user-graduate' : 'fa-user-friends'} text-sm`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {c.type === 'student'
                            ? `${c.class_name || '—'} · ${c.registration_number || 'no reg#'}`
                            : `Parent ${c.cnic ? '· ' + c.cnic : ''}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="text-xs text-teal-600 hover:underline block"
                        >
                          <i className="fas fa-phone mr-1"></i>
                          {c.phone}
                        </a>
                      )}
                      {c.email && (
                        <a
                          href={`mailto:${c.email}`}
                          className="text-xs text-gray-500 hover:underline block truncate max-w-[180px]"
                        >
                          {c.email}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="p-2 border-t bg-gray-50 text-center">
              <Link
                to="/dashboard/contacts"
                className="text-xs text-teal-600 hover:underline font-medium"
              >
                Open full Contacts Directory →
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════
          TWO-COLUMN: Recent Inquiries + Follow-ups
      ═══════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* ── Recent Inquiries ── */}
        <div className="bg-white p-6 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              <i className="fas fa-clipboard-list text-blue-500 mr-2"></i>
              Recent Inquiries
            </h2>
            <Link
              to="/dashboard/inquiries"
              className="text-sm text-blue-600 hover:underline"
            >
              View all →
            </Link>
          </div>

          {receptionLoading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading…</p>
          ) : recentInquiries.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <i className="fas fa-inbox text-3xl mb-2"></i>
              <p className="text-sm">No inquiries recorded yet.</p>
              <button
                onClick={openQuickInquiry}
                className="inline-block mt-3 text-blue-600 hover:underline text-sm"
              >
                Create the first one →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentInquiries.map(row => {
                const typeMeta = INQUIRY_TYPES[row.inquiry_type] || INQUIRY_TYPES.general;
                const statusMeta = STATUS_META[row.status] || STATUS_META.open;
                return (
                  <div
                    key={row.id}
                    className="border-l-4 border-blue-400 pl-3 py-2"
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${typeMeta.color}`}>
                        <i className={`fas ${typeMeta.icon} mr-1`}></i>
                        {typeMeta.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusMeta.color}`}>
                        {statusMeta.label}
                      </span>
                      <span className="text-[10px] text-gray-400 ml-auto">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString()
                          : '—'}
                      </span>
                    </div>
                    <p className="font-semibold text-sm text-gray-800 truncate">
                      {row.visitor_name}
                      {row.child_name && (
                        <span className="text-gray-500 font-normal">
                          {' '}· for {row.child_name}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {row.phone && (
                        <span className="mr-3">
                          <i className="fas fa-phone mr-1"></i>
                          {row.phone}
                        </span>
                      )}
                      {row.class_interested && <span>Class {row.class_interested}</span>}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Upcoming Follow-ups ── */}
        <div className="bg-white p-6 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              <i className="fas fa-clock text-orange-500 mr-2"></i>
              Upcoming Follow Ups
            </h2>
            <span className="text-xs text-gray-500">
              {upcomingFollowUps.length} pending
            </span>
          </div>

          {receptionLoading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading…</p>
          ) : upcomingFollowUps.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <i className="fas fa-check-circle text-3xl mb-2 text-green-300"></i>
              <p className="text-sm">Nothing to follow up right now. 👍</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingFollowUps.map(row => {
                const isToday = row.follow_up_date === todayStr;
                const typeMeta = INQUIRY_TYPES[row.inquiry_type] || INQUIRY_TYPES.general;
                return (
                  <div
                    key={row.id}
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      isToday
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                      isToday
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <span className="text-[9px] uppercase leading-none">
                        {new Date(row.follow_up_date).toLocaleString('en', { month: 'short' })}
                      </span>
                      <span className="text-sm font-bold leading-none mt-0.5">
                        {new Date(row.follow_up_date).getDate()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {row.visitor_name}
                        {isToday && (
                          <span className="ml-2 text-[9px] bg-orange-500 text-white px-1.5 py-0.5 rounded-full">
                            TODAY
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        <i className={`fas ${typeMeta.icon} mr-1`}></i>
                        {typeMeta.label}
                        {row.child_name && ` · ${row.child_name}`}
                      </p>
                      {row.phone && (
                        <a
                          href={`tel:${row.phone}`}
                          className="text-xs text-teal-600 hover:underline mt-0.5 inline-block"
                        >
                          <i className="fas fa-phone mr-1"></i>
                          {row.phone}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
          RECENT ANNOUNCEMENTS
      ═══════════════════════════════════════ */}
      <div className="bg-white p-6 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            <i className="fas fa-bullhorn text-teal-500 mr-2"></i>
            Recent Announcements
          </h2>
          <Link
            to="/dashboard/notifications"
            className="text-sm text-teal-600 hover:underline"
          >
            View all →
          </Link>
        </div>

        {announcementsLoading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : recentAnnouncements.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-bell-slash text-4xl mb-3"></i>
            <p>No announcements yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentAnnouncements.map(a => (
              <div key={a.id} className="border-l-4 border-teal-400 pl-3 py-2">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-800">{a.title}</p>
                  {!a.is_read && (
                    <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">
                      NEW
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mt-1">{a.message}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {a.posted_by_name} · {a.created_at_display}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════
          QUICK INQUIRY MODAL
      ═══════════════════════════════════════ */}
      <Modal
        isOpen={showQuickInquiry}
        onClose={() => { setShowQuickInquiry(false); setQuickForm(EMPTY_FORM); }}
        title="New Admission / Inquiry"
        onSave={saveQuickInquiry}
        saveLabel={savingQuick ? 'Saving…' : 'Record'}
        size="lg"
      >
        <div className="space-y-4">

          {/* ── Visitor Details ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Visitor Details
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Visitor Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full p-2 border rounded"
                  placeholder="e.g. Ahmed Khan"
                  value={quickForm.visitor_name}
                  onChange={e => setQuickForm({ ...quickForm, visitor_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input
                  className="w-full p-2 border rounded"
                  placeholder="+92-300-1234567"
                  value={quickForm.phone}
                  onChange={e => setQuickForm({ ...quickForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="visitor@example.com"
                value={quickForm.email}
                onChange={e => setQuickForm({ ...quickForm, email: e.target.value })}
              />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* ── Inquiry Details ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Inquiry Details
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Inquiry Type</label>
                <select
                  className="w-full p-2 border rounded"
                  value={quickForm.inquiry_type}
                  onChange={e => setQuickForm({ ...quickForm, inquiry_type: e.target.value })}
                >
                  {INQUIRY_TYPE_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={quickForm.status}
                  onChange={e => setQuickForm({ ...quickForm, status: e.target.value })}
                >
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* ── Child / Student Info ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Student / Child Information
              <span className="ml-1 text-gray-400 font-normal normal-case">(optional)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Child's Full Name</label>
                <input
                  className="w-full p-2 border rounded"
                  placeholder="e.g. Bilal Ahmed"
                  value={quickForm.child_name}
                  onChange={e => setQuickForm({ ...quickForm, child_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Class Interested In</label>
                <input
                  className="w-full p-2 border rounded"
                  placeholder="e.g. 5-A or Grade 1"
                  value={quickForm.class_interested}
                  onChange={e => setQuickForm({ ...quickForm, class_interested: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Child's Date of Birth</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={quickForm.child_dob}
                  onChange={e => setQuickForm({ ...quickForm, child_dob: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Follow‑up Date
                  <span className="ml-1 text-gray-400 font-normal">(when to contact again)</span>
                </label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={quickForm.follow_up_date}
                  onChange={e => setQuickForm({ ...quickForm, follow_up_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* ── Notes ── */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              className="w-full p-2 border rounded"
              rows="3"
              placeholder="Any additional details about the visit, requirements, or concerns…"
              value={quickForm.notes}
              onChange={e => setQuickForm({ ...quickForm, notes: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}